import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../../services/api';
import NoClientState from './NoClientState';
import './ai-vis.css';

// ── Step definitions ──────────────────────────────────────────────────────────

const ONBOARDING_STEPS = [
  {
    key: 'campaign_kickoff',
    title: 'Campaign Kickoff',
    description: 'The AI SEO campaign is officially live and underway.',
    recurring: false,
  },
  {
    key: 'digital_pr_questionnaire',
    title: 'Digital PR Questionnaire Form',
    description:
      'This questionnaire will provide us with key information that we can use to effectively build brand mentions for your business.',
    recurring: false,
  },
  {
    key: 'client_overview_form',
    title: 'Client Overview Form',
    description:
      "Contains research about your brand to make sure we fully understand your business. We'll be referencing this information throughout the AI SEO campaign to make sure everything we do is 100% aligned with your brand.",
    recurring: false,
    unlocksAfter: 'client_overview_form',
  },
  {
    key: 'prompt_research',
    title: 'Prompt Research',
    description:
      'List of prompts your customers are likely asking ChatGPT and other AI search engines. The prompts will serve as the foundation of the AI SEO campaign, and will impact all aspects of the AI SEO campaign.',
    unlockedBy: 'client_overview_form',
  },
  {
    key: 'reddit_prospecting',
    title: 'Reddit Marketing',
    description:
      'Identify Reddit threads that AI assistants rely on when recommending products in your category. We\'ll then create and post natural comments that subtly mention {{client_name}}.',
    unlockedBy: 'client_overview_form',
  },
  {
    key: 'content_strategy',
    title: 'Content Strategy',
    description:
      'Contains a list of highly citable blog topics that we can create content around to influence ChatGPT and other AI assistants.',
    unlockedBy: 'client_overview_form',
  },
  {
    key: 'brand_mention_prospecting',
    title: 'Brand Mention Outreach',
    description:
      'Begin pitching {{client_name}} to be included in the "best of" listicles that AI assistants are actively relying on when recommending products. Getting included in these listicles can have a significant impact on your AI visibility.',
    unlockedBy: 'client_overview_form',
  },
];

// ── Status helpers ────────────────────────────────────────────────────────────

const STATUS_META = {
  // generic
  awaiting:           { label: 'Awaiting',               variant: 'awaiting' },
  pending:            { label: 'Pending',                variant: 'awaiting' },
  // in-progress
  sent:               { label: 'Sent',                   variant: 'in-progress' },
  pm_approval:        { label: 'PM Approval Needed',     variant: 'in-progress' },
  pm_approval_needed: { label: 'PM Approval Needed',     variant: 'in-progress' },
  pm_approved:        { label: 'PM Approved',            variant: 'in-progress' },
  sent_to_client:     { label: 'Sent to Client',         variant: 'client' },
  // completed
  approved:           { label: 'Approved',               variant: 'completed' },
  approved_by_client: { label: 'Approved by Client',     variant: 'completed' },
  completed:          { label: 'Completed',              variant: 'completed' },
};

function statusMeta(status) {
  if (!status) return { label: 'Awaiting', variant: 'awaiting' };
  return STATUS_META[status] ?? { label: status.replace(/_/g, ' '), variant: 'in-progress' };
}

function isComplete(status) {
  return ['approved', 'approved_by_client', 'completed'].includes(status);
}

function isActive(status) {
  return status && !isComplete(status) && status !== 'awaiting' && status !== 'pending';
}

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const { label, variant } = statusMeta(status);
  return (
    <span className={`aivis-delivery-badge aivis-delivery-badge--${variant}`}>
      <span className="aivis-delivery-badge-dot" />
      {label}
    </span>
  );
}

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function StepCard({ stepDef, stepData, locked, clientName }) {
  const status = stepData?.status ?? null;
  const completed = isComplete(status);
  const active = isActive(status);

  const iconVariant = completed ? 'completed' : active ? 'active' : 'pending';
  const cardClass = [
    'aivis-delivery-step',
    completed ? 'aivis-delivery-step--completed' : '',
    locked ? 'aivis-delivery-step--locked' : '',
  ].filter(Boolean).join(' ');

  const description = (stepDef.description || '').replace(/\{\{client_name\}\}/g, clientName || 'your brand');

  return (
    <div className={cardClass}>
      <div className={`aivis-delivery-step-icon aivis-delivery-step-icon--${iconVariant}`}>
        {completed ? <CheckIcon /> : <ClockIcon />}
      </div>
      <div className="aivis-delivery-step-body">
        <div className="aivis-delivery-step-top">
          <span className="aivis-delivery-step-title">{stepDef.title}</span>
          <StatusBadge status={status || 'awaiting'} />
        </div>
        <p className="aivis-delivery-step-desc">{description}</p>
        {(stepData?.updated_at || stepData?.notes) && (
          <div className="aivis-delivery-step-meta">
            {stepData.updated_at && completed && (
              <span className="aivis-delivery-step-time">
                ✓ {statusMeta(status).label} · {formatDate(stepData.updated_at)}
              </span>
            )}
            {stepData.updated_at && !completed && active && (
              <span className="aivis-delivery-step-time">
                Updated · {formatDate(stepData.updated_at)}
              </span>
            )}
          </div>
        )}
        {stepData?.notes && (
          <div className="aivis-delivery-step-notes">{stepData.notes}</div>
        )}
      </div>
    </div>
  );
}

// ── Progress bar ──────────────────────────────────────────────────────────────

function ProgressBar({ steps, stepMap }) {
  const total = steps.length;
  const done = steps.filter(s => isComplete(stepMap[s.key]?.status)).length;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);

  return (
    <div className="aivis-delivery-progress">
      <div className="aivis-delivery-progress-row">
        <div>
          <div className="aivis-delivery-progress-count">
            {done}<span>/{total}</span>
          </div>
          <div className="aivis-delivery-progress-label">Steps Complete</div>
        </div>
        <div className="aivis-delivery-progress-pct">{pct}%</div>
      </div>
      <div className="aivis-delivery-progress-bar-track">
        <div
          className="aivis-delivery-progress-bar-fill"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ── Onboarding tab ────────────────────────────────────────────────────────────

function OnboardingTab({ stepMap, clientName }) {
  const clientOverviewComplete = isComplete(stepMap['client_overview_form']?.status);

  return (
    <div className="aivis-delivery-steps">
      {ONBOARDING_STEPS.map((stepDef) => {
        const isLocked = stepDef.unlockedBy === 'client_overview_form' && !clientOverviewComplete;
        return (
          <React.Fragment key={stepDef.key}>
            {isLocked && stepDef.key === 'prompt_research' && (
              <div className="aivis-delivery-unlock-label">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m9 18 6-6-6-6" />
                </svg>
                Unlocks once <strong style={{ marginLeft: 3, marginRight: 3 }}>Client Overview Form</strong> is approved
              </div>
            )}
            <StepCard
              stepDef={stepDef}
              stepData={stepMap[stepDef.key]}
              locked={isLocked}
              clientName={clientName}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CampaignOverviewPage() {
  const { selectedCampaignId, campaigns } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const selectedCampaign = campaigns?.find(c => String(c.id) === String(selectedCampaignId));

  const fetchDelivery = useCallback(async () => {
    if (!selectedCampaignId) return;
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.get(`/aivis/campaigns/${selectedCampaignId}/delivery/`);
      setData(res);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load campaign overview.');
    } finally {
      setLoading(false);
    }
  }, [selectedCampaignId]);

  useEffect(() => {
    fetchDelivery();
  }, [fetchDelivery]);

  // Build onboarding step map for quick lookup
  const onboardingMap = {};
  if (data?.steps) {
    data.steps.forEach(s => {
      if (s.phase === 'onboarding') onboardingMap[s.step] = s;
    });
  }

  const clientName = selectedCampaign?.client_name || data?.client_name || 'Campaign';

  if (!selectedCampaignId) {
    return <NoClientState />;
  }

  return (
    <div className="aivis-delivery">
      <div className="aivis-delivery-header">
        <h1 className="aivis-delivery-title">Overview</h1>
        <p className="aivis-delivery-subtitle">{clientName} — delivery milestones and status</p>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 16px',
        marginBottom: 20,
        background: 'rgba(234, 179, 8, 0.08)',
        border: '1px solid rgba(234, 179, 8, 0.35)',
        borderRadius: 10,
        fontSize: 13,
        color: 'var(--aivis-caution-ink)',
      }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: 'var(--aivis-caution)' }}>
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <span><strong style={{ fontWeight: 600 }}>Currently in Development</strong> — This page is a work in progress. Content may be incomplete or subject to change.</span>
      </div>

      {loading && (
        <div className="aivis-delivery-loading">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}>
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          Loading…
        </div>
      )}

      {error && !loading && (
        <div style={{ color: 'var(--aivis-danger)', fontSize: 13, padding: '12px 16px', background: 'rgba(220,38,38,0.06)', borderRadius: 10, border: '1px solid rgba(220,38,38,0.18)' }}>
          {error}
        </div>
      )}

      {!loading && !error && (
        <OnboardingTab stepMap={onboardingMap} clientName={clientName} />
      )}
    </div>
  );
}
